<template>
  <div :class="classes">
    <div class="e-text-editor__inner">
      <e-label>{{ label }}</e-label>
      <!--      {{ files }}-->
      <editor-content :editor="editor"></editor-content>
      <div class="e-text-editor__files-container">
        <e-attachment
          v-for="file in files"
          :file="file"
          :key="file.id"
          @click:delete="$emit('click:delete-file', file)"
        />
      </div>
      <div class="e-text-editor__actions">
        <div class="e-text-editor__actions-group">
          <e-button
            v-if="attachment"
            class="mr-1"
            variant="ghost"
            icon="icon-attachment"
            small
            @click="$emit('click:upload')"
          />
          <e-button
            v-if="bold"
            class="mr-1"
            :class="{ isActive: editor && editor.isActive('bold') && editor.isFocused }"
            variant="ghost"
            icon="icon-text-bold"
            :disabled="isDisabled"
            @click="editor.chain().focus().toggleBold().run()"
            small
          />
          <e-button
            v-if="italic"
            class="mr-1"
            :class="{ isActive: editor && editor.isActive('italic') && editor.isFocused }"
            variant="ghost"
            icon="icon-text-italic"
            :disabled="isDisabled"
            @click="editor.chain().focus().toggleItalic().run()"
            small
          />
          <e-button
            v-if="strikethrough"
            class="mr-1"
            :class="{ isActive: editor && editor.isActive('strike') && editor.isFocused }"
            variant="ghost"
            icon="icon-text-strikethrough"
            :disabled="isDisabled"
            @click="editor.chain().focus().toggleStrike().run()"
            small
          />
          <e-button
            v-if="orderedList"
            class="mr-1"
            :class="{ isActive: editor && editor.isActive('orderedList') && editor.isFocused }"
            variant="ghost"
            icon="icon-numbered-list"
            :disabled="isDisabled"
            @click="editor.chain().focus().toggleOrderedList().run()"
            small
          />
          <e-button
            v-if="bulletList"
            class="mr-1"
            :class="{ isActive: editor && editor.isActive('bulletList') && editor.isFocused }"
            variant="ghost"
            icon="icon-bullet-list"
            :disabled="isDisabled"
            @click="editor.chain().focus().toggleBulletList().run()"
            small
          />

          <e-menu v-model="menu" offset-y :close-on-content-click="false">
            <template #activator="{ on, attrs }">
              <e-button
                v-if="link"
                class="mr-1"
                :class="{ isActive: editor && editor.isActive('link') && editor.isFocused }"
                variant="ghost"
                icon="icon-chain-link"
                :disabled="isDisabled"
                v-bind="attrs"
                v-on="on"
                @click="handleLink"
                small
              />
            </template>
            <div class="d-flex pa-1">
              <e-text-field v-model="linkInner" @keyup.enter="submitLink" class="mr-1" />
              <e-button icon="icon-paper-plane" @click="submitLink"></e-button>
            </div>
          </e-menu>
          <e-button
            v-if="blockquote"
            class="mr-1"
            :class="{ isActive: editor && editor.isActive('blockquote') && editor.isFocused }"
            variant="ghost"
            icon="icon-quotation-marks"
            :disabled="isDisabled"
            @click="editor.chain().focus().toggleBlockquote().run()"
            small
          />
        </div>
        <div v-if="submitButton" class="e-text-editor__actions-group">
          <e-button
            variant="primary"
            icon="icon-paper-plane"
            :disabled="(editor && !editor.getText().length) || validationState === 'error'"
            @click="$emit('submit')"
            small
          />
        </div>
      </div>
    </div>
    <div v-if="showDetails" class="e-text-editor__details">
      <v-messages :color="validationState" :value="messagesToDisplay"></v-messages>
      <v-counter v-if="counter" :max="counter" :value="computedCounterValue"></v-counter>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { VMessages, VCounter } from 'vuetify/lib/components'
import Validatable from 'vuetify/lib/mixins/validatable'
import { ELabel } from '../index'
import { ETextField, EButton } from '../index'
import { EMenu } from '../EMenu'
import EAttachment from './EAttachment'

export default {
  name: 'ETextEditor',
  mixins: [Validatable],
  components: {
    EAttachment,
    EditorContent,
    ELabel,
    VMessages,
    VCounter,
    EButton,
    EMenu,
    ETextField,
  },

  props: {
    attachment: {
      type: Boolean,
      default: false,
    },
    bold: {
      type: Boolean,
      default: false,
    },
    italic: {
      type: Boolean,
      default: false,
    },
    strikethrough: {
      type: Boolean,
      default: false,
    },
    orderedList: {
      type: Boolean,
      default: false,
    },
    bulletList: {
      type: Boolean,
      default: false,
    },
    link: {
      type: Boolean,
      default: false,
    },
    blockquote: {
      type: Boolean,
      default: false,
    },
    submitButton: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    counter: {
      type: [Number, String],
      default: null,
    },
    hint: String,
    hideDetails: {
      type: [Boolean, String],
      default: 'auto',
    },
    textarea: {
      type: Boolean,
      default: false,
    },
    files: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      // files: [],
      editor: null,
      linkInner: '',
      menu: false,
    }
  },

  computed: {
    classes() {
      return {
        'e-text-editor': true,
        'e-text-editor--is-focused': this.editor && this.editor.isFocused,
        'e-text-editor--error': this.validationState === 'error',
        'e-text-editor--success': this.validationState === 'success',
        'e-text-editor--disabled': this.isDisabled,
      }
    },
    messagesToDisplay() {
      if (this.hasHint) return [this.hint]
      if (!this.hasMessages) return []
      return this.validations
        .map((validation) => {
          if (typeof validation === 'string') return validation
          const validationResult = validation(this.internalValue)
          return typeof validationResult === 'string' ? validationResult : ''
        })
        .filter((message) => message !== '')
    },
    showDetails() {
      return this.hideDetails === false || (this.hideDetails === 'auto' && this.hasDetails)
    },
    hasDetails() {
      return this.messagesToDisplay.length > 0 || this.hasCounter
    },
    computedCounterValue() {
      return [...((this.editor && this.editor.getText()) || '').toString()].length
    },

    hasCounter() {
      return this.counter !== false && this.counter !== null
    },
  },
  methods: {
    drop(event) {
      // event.preventDefault()
      // //duplicate the upload plugin structure as close as possible
      // console.log('event.dataTransfer.files', event.dataTransfer.files)
      // let filesRemapped = [...event.dataTransfer.files].map((file) => {
      //   let fileMapped = {
      //     fileObject: true,
      //     name: file.name,
      //     type: file.type,
      //     progress: 0,
      //     file,
      //     active: false,
      //     dropType: 'standart',
      //     error: '',
      //     success: undefined,
      //     timeout: 0,
      //     extension: 'zip',
      //     loading: false,
      //     postAction: undefined,
      //     putAction: undefined,
      //     responseId: undefined,
      //     response: {},
      //     size: file.size,
      //     speed: 0,
      //     data: { droped: true },
      //     headers: {},
      //     id: String(Math.floor(Math.random() * (0 - 99999999 + 1))),
      //   }
      //   return fileMapped
      // })
      // this.files = [...filesRemapped]
      // this.$emit('upload', this.files)
      //place files in store for loaders and such
      // this.files = [...filesRemapped, ...this.files]
      //send off files to file uploader function
    },
    triggerUpload() {
      this.$refs.upload2.$children[0].$el.click()
      // this.$refs.upload3.$el.click()
    },
    fileUpload(file) {
      // this.files = [...file]
      this.$emit('upload', [...file])
    },
    handleLink() {
      if (this.editor.isActive('link')) {
        this.editor.chain().focus().unsetLink().run()
        this.menu = false
        return
      }
      this.linkInner = this.editor.getAttributes('link').href
      this.menu = true
    },
    submitLink() {
      if (this.linkInner === null) {
        this.menu = false
        return
      }

      // empty
      if (this.linkInner === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
        this.menu = false
        return
      }
      // update link
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: this.linkInner }).run()
      this.menu = false
    },
  },

  watch: {
    placeholder() {
      this.editor.commands.setContent(this.value, false)
    },
    value(value) {
      let isSame = ''
      // HTML
      if (this.textarea) {
        isSame = this.editor.getText() === value
      } else {
        isSame = this.editor.getHTML() === value
      }

      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

      if (isSame) {
        return
      }

      this.editor.commands.setContent(value, false)
    },
  },

  mounted() {
    this.editor = new Editor({
      editable: !this.disabled,
      content: this.value,
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
        }),
        Placeholder.configure({
          // Use a placeholder:
          placeholder: this.placeholder,
        }),
      ],
      onUpdate: () => {
        this.$emit('input', this.textarea ? this.editor.getText() : this.editor.getHTML())
      },
    })
  },

  beforeDestroy() {
    this.editor.destroy()
  },
}
</script>
